import { useState, useEffect } from 'react';
import { api } from "../util/APIConfig";
import Select from 'react-select';

function ESATSpecificationModal({ isOpen, onClose }) {
  const [selectedParts, setSelectedParts] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [esatParts, setEsatParts] = useState([]);
  const [isLoadingParts, setIsLoadingParts] = useState(true);

  useEffect(() => {
    const fetchESATParts = async () => {
      setIsLoadingParts(true);
      try {
        const response = await api.get("/Subject/SectionNumber/GetESATParts");
        // Assuming the API returns an array of parts with id and name
        const formattedParts = response.data.data.map(part => ({
          value: part.id,
          label: part.sectionNo
        }));
        setEsatParts(formattedParts);
      } catch (error) {
        console.error("Error fetching ESAT parts:", error);
        //alert("Failed to load ESAT parts. Please refresh the page.");
      } finally {
        setIsLoadingParts(false);
      }
    };

    if (isOpen) {
      fetchESATParts();
    }
  }, [isOpen]);

  const handleChange = (selected) => {
    // Limit to 2 selections
    if (selected && selected.length <= 2) {
      setSelectedParts(selected);
    }
  };

  const handleSubmit = async () => {
    if (selectedParts.length !== 2) {
      alert("Please select exactly 2 ESAT parts");
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post("/Subject/AdmissionTest/SetESATParts", {
        selectedParts: selectedParts.map(part => part.value)
      });
      onClose();
    } catch (error) {
      console.error("Error saving ESAT parts:", error);
      //alert("Failed to save your ESAT part selections. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-auto">
        <div className="p-6 sm:p-8">
          <div className="mb-6">
            <h2 className="text-xl sm:text-lg font-bold text-gray-900 mb-2 flex items-center gap-2 flex-wrap">
              Which Parts of the ESAT are you taking?
              <span className="text-red-500 text-sm font-medium bg-red-50 px-2 py-0.5 rounded">Required</span>
            </h2>
            <p className="text-gray-600 text-sm sm:text-base">
              Please select which parts of the ESAT you will be taking. This will remove any irrelevant content from the rest of your student portal. But don't worry if you change your mind, you can update your access at any time in the 'Question Bank' section of the portal.
            </p>
          </div>

          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select 2 ESAT parts:
          </label>
          <Select
            isMulti
            value={selectedParts}
            onChange={handleChange}
            options={esatParts}
            className="mb-6"
            classNamePrefix="react-select"
            placeholder={isLoadingParts ? "Loading ESAT parts..." : "Select 2 ESAT parts..."}
            noOptionsMessage={() => 
              isLoadingParts 
                ? "Loading..." 
                : esatParts.length === 0 
                  ? "No parts available" 
                  : "No more parts available"
            }
            isDisabled={isLoadingParts}
            isOptionDisabled={() => selectedParts.length >= 2}
            styles={{
              control: (base) => ({
                ...base,
                borderColor: '#e2e8f0',
                '&:hover': {
                  borderColor: '#cbd5e1',
                },
                padding: '2px',
              }),
              multiValue: (base) => ({
                ...base,
                backgroundColor: '#e2e8f0',
                borderRadius: '4px',
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#e2e8f0' : 'white',
              }),
              loadingMessage: (base) => ({
                ...base,
                color: '#6b7280',
              }),
            }}
            loadingMessage={() => "Loading ESAT parts..."}
          />

          <div className="mt-6">
            <div className="flex items-center mb-4">
              <div className={`w-full h-2 rounded-full ${
                selectedParts.length === 0 ? 'bg-gray-200' :
                selectedParts.length === 1 ? 'bg-yellow-400' :
                'bg-green-500'
              }`}/>
              <span className="ml-3 text-sm text-gray-600 min-w-[80px]">
                {selectedParts.length}/2 selected
              </span>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              {selectedParts.length === 2 
                ? "Your selections are complete. Click confirm to continue." 
                : `Please select ${2 - selectedParts.length} more part${selectedParts.length === 1 ? '' : 's'} to continue.`}
            </p>

            <button
              onClick={handleSubmit}
              disabled={selectedParts.length !== 2 || isSubmitting}
              className={`w-full px-4 py-3 rounded-lg flex items-center justify-center ${
                selectedParts.length === 2 && !isSubmitting
                  ? 'bg-blue-500 text-white hover:bg-blue-600 transform transition-all duration-200 hover:scale-[1.02]'
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isSubmitting ? (
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                'Continue'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ESATSpecificationModal;